import { User } from '../types';

// Normalize various role labels to strict union used by the app
const normalizeRole = (role?: string | null): 'superadmin' | 'basicadmin' | null => {
	const r = String(role || '').trim().toLowerCase();
	if (!r) return null;
	if (
		r === 'superadmin' || r === 'super-admin' || r === 'system_admin' ||
		r === 'systemadmin' || r === 'system-admin' || r === 'admin' ||
		r === 'owner' || r === 'root'
	) return 'superadmin';
	if (r === 'basicadmin' || r === 'basic-admin' || r === 'basic_admin' || r === 'basic') return 'basicadmin';
	return null;
};

function getLocal(key: string): string | null {
	try {
		if (typeof window === 'undefined') return null;
		return localStorage.getItem(key);
	} catch { return null; }
}

// Prefer CURRENT_USER.adminRole (server-provided). Only honor dev_admin_role in development builds.
export const resolveAdminRole = (user?: User | null): 'superadmin' | 'basicadmin' | null => {
	// 1) Direct from user (preferred)
	const direct = normalizeRole((user as any)?.adminRole);
	if (direct) return direct;
	// 2) Cached CURRENT_USER from previous /api/me
	try {
		const raw = getLocal('CURRENT_USER');
		if (raw) {
			const parsed: any = JSON.parse(String(raw));
			const r = normalizeRole(parsed?.adminRole);
			if (r) return r;
		}
	} catch {}
	// 3) Dev override only in dev environment
	try {
		const isDev = (import.meta as any)?.env?.DEV === true;
		if (isDev) {
			const dev = getLocal('dev_admin_role');
			const r = normalizeRole(dev);
			if (r) return r;
		}
	} catch {}
	return null;
};

export const hasAdminToken = (): boolean => {
	try {
		if (typeof window === 'undefined') return false;
		return !!localStorage.getItem('dev_admin_token');
	} catch { return false; }
};

export const isSuperAdmin = (user?: User | null) => resolveAdminRole(user) === 'superadmin';
export const isBasicAdmin = (user?: User | null) => resolveAdminRole(user) === 'basicadmin';

// Permissions mapping
export const canCreateUserCards = (user?: User | null) => isSuperAdmin(user) || isBasicAdmin(user);
export const canCustomizeCards = (user?: User | null) => isSuperAdmin(user) || isBasicAdmin(user);
export const canDeleteUsers = (user?: User | null) => isSuperAdmin(user);

export const canManageAds = (user?: User | null) => isSuperAdmin(user) || isBasicAdmin(user);
export const canDeleteAds = (user?: User | null) => isSuperAdmin(user);

export const canManageSubscriptions = (user?: User | null) => isSuperAdmin(user) || isBasicAdmin(user);
export const canDeleteSubscriptions = (user?: User | null) => isSuperAdmin(user);

export const canManageTemplates = (user?: User | null) => isSuperAdmin(user) || isBasicAdmin(user);
export const canDeleteTemplates = (user?: User | null) => isSuperAdmin(user);

export const canDownloadLogs = (user?: User | null) => isSuperAdmin(user) || isBasicAdmin(user);

export const canSendBroadcasts = (user?: User | null) => isSuperAdmin(user) || isBasicAdmin(user);

// Moderation: requires superadmin approval for broadcasts from basic admin
export const requiresApprovalForBroadcast = (user?: User | null) => isBasicAdmin(user) && !isSuperAdmin(user);

// Activity logging helper (frontend guidance)
export const shouldLogActivity = (user?: User | null) => isBasicAdmin(user) || isSuperAdmin(user);

/* ────────── Task-based permissions for admin managers ────────── */
type ManagerTask = 'finance' | 'subscriptions' | 'mini_websites' | 'crm' | 'marketing' | 'payments_settings' | 'support';

/** Resolve the current admin's assigned tasks from user object or localStorage cache */
export const resolveAssignedTasks = (user?: User | null): ManagerTask[] => {
	if ((user as any)?.assignedTasks) return (user as any).assignedTasks;
	try {
		const raw = getLocal('CURRENT_USER');
		if (raw) {
			const parsed = JSON.parse(raw);
			if (parsed?.assignedTasks) return parsed.assignedTasks;
		}
	} catch {}
	return [];
};

/** Check if admin has a specific task or is superadmin (superadmin bypasses all checks) */
export const hasTask = (user: User | null | undefined, task: ManagerTask): boolean => {
	if (isSuperAdmin(user)) return true;
	return resolveAssignedTasks(user).includes(task);
};

export const canAccessFinance = (user?: User | null) => hasTask(user, 'finance');
export const canAccessSubscriptions = (user?: User | null) => hasTask(user, 'subscriptions');
export const canAccessMiniWebsites = (user?: User | null) => hasTask(user, 'mini_websites');
export const canAccessCRM = (user?: User | null) => hasTask(user, 'crm');
export const canAccessMarketing = (user?: User | null) => hasTask(user, 'marketing');
export const canAccessPaymentsSettings = (user?: User | null) => hasTask(user, 'payments_settings');
export const canAccessSupport = (user?: User | null) => hasTask(user, 'support');
